import React, { useEffect, useMemo, useState } from 'react';
import { obtenerInformeCargas } from './api/catalogo';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, Search, RefreshCw, AlertTriangle, ChevronDown, ChevronRight, ArrowUp, ArrowDown, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';
import './InformeCarga.css';

const DIAS_VENTANA = 45;

const fmtFecha = (fechaStr) => fechaStr ? fechaStr.split('-').reverse().join('/') : '—';

/* ── InformeCarga ──────────────────────────────────────────────
   Tres vistas sobre las cargas ('load_events') de los últimos
   DIAS_VENTANA días:
   - "Dashboard": modelos/versiones cargados por marca, comparando la
     última carga contra la inmediatamente anterior — de un vistazo,
     qué marcas subieron o bajaron de volumen.
   - "Última carga": qué marcas cargaron qué modelos en la carga más
     reciente, en una sola tabla ordenable — responde "qué entró".
   - "Histórico de cambios": compara cada carga contra la inmediatamente
     anterior y acumula, por marca, qué versiones aparecieron por primera
     vez ("nuevas") y cuáles dejaron de verse ("fallaron") — para detectar
     patrones recurrentes y no solo lo que pasó en la última carga. */
function InformeCarga() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('dashboard'); // 'dashboard' | 'ultima' | 'historico'
  const [fechasUsadas, setFechasUsadas] = useState([]);
  const [ultimaCarga, setUltimaCarga] = useState(null);
  const [resumenPorMarca, setResumenPorMarca] = useState([]);
  const [comparativoMarcas, setComparativoMarcas] = useState([]);

  // -- tab "dashboard" --
  const [metricaDashboard, setMetricaDashboard] = useState('versiones'); // 'versiones' | 'modelos'
  const [busquedaDashboard, setBusquedaDashboard] = useState('');

  // -- tab "última carga" --
  const [busquedaUltima, setBusquedaUltima] = useState('');
  const [ordenUltima, setOrdenUltima] = useState({ campo: 'marca', dir: 'asc' });

  // -- tab "histórico" --
  const [marcaExpandida, setMarcaExpandida] = useState(null);
  const [soloRecurrentes, setSoloRecurrentes] = useState(true);
  const [busquedaHistorico, setBusquedaHistorico] = useState('');

  const cargarInforme = async () => {
    setLoading(true); setError(''); setMarcaExpandida(null);
    try {
      const {
        fechasUsadas: fechas, resumenPorMarca: resumen, ultimaCarga: ultima,
        comparativoMarcas: comparativo,
      } = await obtenerInformeCargas(DIAS_VENTANA);
      if (!ultima) {
        setError('No hay cargas registradas en los últimos días.');
        setResumenPorMarca([]); setFechasUsadas([]); setUltimaCarga(null); setComparativoMarcas([]);
        return;
      }
      setFechasUsadas(fechas);
      setResumenPorMarca(resumen);
      setUltimaCarga(ultima);
      setComparativoMarcas(comparativo || []);
    } catch (e) {
      console.error(e);
      setError('No se pudo generar el informe. Intenta de nuevo.');
      toast.error('Error generando el informe de cargas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargarInforme(); }, []);

  // ── tab "dashboard": comparativo por marca actual vs anterior ──
  const filasDashboard = useMemo(() => {
    const term = busquedaDashboard.trim().toLowerCase();
    const filtradas = term ? comparativoMarcas.filter(m => m.marca.toLowerCase().includes(term)) : comparativoMarcas;
    return [...filtradas].sort((a, b) => b.actual.versiones - a.actual.versiones || a.marca.localeCompare(b.marca));
  }, [comparativoMarcas, busquedaDashboard]);

  const chartDashboard = useMemo(() =>
    filasDashboard.slice(0, 15).map(m => ({
      marca: m.marca,
      actual: m.actual[metricaDashboard],
      anterior: m.anterior[metricaDashboard],
    })), [filasDashboard, metricaDashboard]);

  const marcasSubieron = useMemo(() =>
    comparativoMarcas.filter(m => m.actual.versiones > m.anterior.versiones).length, [comparativoMarcas]);
  const marcasBajaron = useMemo(() =>
    comparativoMarcas.filter(m => m.actual.versiones < m.anterior.versiones && m.anterior.versiones > 0).length, [comparativoMarcas]);

  // ── tab "última carga": tabla plana marca+modelo+versión ──
  const filasUltimaCarga = useMemo(() => {
    if (!ultimaCarga) return [];
    const filas = ultimaCarga.marcas.flatMap(m =>
      m.modelos.map(v => ({ marca: m.marca, model: v.model, modelDetail: v.modelDetail })));
    const term = busquedaUltima.trim().toLowerCase();
    const filtradas = term
      ? filas.filter(f => f.marca.toLowerCase().includes(term) || f.model.toLowerCase().includes(term))
      : filas;
    const { campo, dir } = ordenUltima;
    const mul = dir === 'asc' ? 1 : -1;
    return [...filtradas].sort((a, b) =>
      (a[campo] || '').localeCompare(b[campo] || '') * mul ||
      a.marca.localeCompare(b.marca) || a.model.localeCompare(b.model));
  }, [ultimaCarga, busquedaUltima, ordenUltima]);

  const marcasUnicasUltima = useMemo(() =>
    ultimaCarga ? ultimaCarga.marcas.length : 0, [ultimaCarga]);

  const toggleOrdenUltima = (campo) => {
    setOrdenUltima(prev => prev.campo === campo
      ? { campo, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { campo, dir: 'asc' });
  };
  const flechaOrden = (campo) => ordenUltima.campo !== campo ? null
    : (ordenUltima.dir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />);

  // ── tab "histórico": resumen por marca (nuevos + faltantes) ──
  const filasHistorico = useMemo(() => {
    const term = busquedaHistorico.trim().toLowerCase();
    return resumenPorMarca.filter(m => {
      if (soloRecurrentes && m.vecesConFaltantes < 2 && m.vecesConNuevos < 2) return false;
      if (!term) return true;
      return m.marca.toLowerCase().includes(term);
    });
  }, [resumenPorMarca, soloRecurrentes, busquedaHistorico]);

  const marcasRecurrentes = useMemo(() =>
    resumenPorMarca.filter(m => m.vecesConFaltantes >= 2 || m.vecesConNuevos >= 2).length, [resumenPorMarca]);

  const detalleUnificado = (m) => {
    const nuevos = m.detalleNuevos.map(d => ({ ...d, tipo: 'nuevo' }));
    const faltantes = m.detalleFaltantes.map(d => ({ ...d, tipo: 'faltante' }));
    return [...nuevos, ...faltantes].sort((a, b) =>
      b.fechaActual.localeCompare(a.fechaActual) || a.model.localeCompare(b.model));
  };

  return (
    <div className="informe-main">
      <div className="informe-header">
        <div className="informe-header-left">
          <Link to="/modelos" className="informe-back" title="Volver al catálogo"><ArrowLeft size={16} /></Link>
          <div>
            <h2 className="informe-title">Informe de cargas — últimos {DIAS_VENTANA} días</h2>
            <p className="informe-meta">
              {fechasUsadas.length > 0
                ? <>{fechasUsadas.length} cargas registradas · última: {fmtFecha(fechasUsadas[fechasUsadas.length - 1])}</>
                : `Últimos ${DIAS_VENTANA} días`}
            </p>
          </div>
        </div>
        <button className="informe-btn" onClick={cargarInforme} disabled={loading}>
          <RefreshCw size={14} /> {loading ? 'Cargando…' : 'Recargar'}
        </button>
      </div>

      {!loading && !error && (
        <div className="informe-tabs">
          <button className={`informe-tab ${tab === 'dashboard' ? 'informe-tab--active' : ''}`} onClick={() => setTab('dashboard')}>
            Dashboard
          </button>
          <button className={`informe-tab ${tab === 'ultima' ? 'informe-tab--active' : ''}`} onClick={() => setTab('ultima')}>
            Última carga
          </button>
          <button className={`informe-tab ${tab === 'historico' ? 'informe-tab--active' : ''}`} onClick={() => setTab('historico')}>
            Histórico de cambios
          </button>
        </div>
      )}

      {loading && (
        <div className="informe-state">
          <div className="informe-state-icon">⏳</div>
          <p>Generando el informe de los últimos {DIAS_VENTANA} días… puede tardar unos segundos.</p>
        </div>
      )}

      {!loading && error && (
        <div className="informe-state">
          <div className="informe-state-icon">⚠️</div>
          <p>{error}</p>
        </div>
      )}

      {!loading && !error && tab === 'dashboard' && (
        <>
          <div className="informe-summary">
            <div className="informe-summary-card informe-summary-card--ok">
              <span className="informe-summary-num">{fmtFecha(ultimaCarga?.loadedAt)}</span>
              <span className="informe-summary-label">última carga · "anterior" es la última vez que se vio cada marca antes de esta fecha</span>
            </div>
            <div className="informe-summary-card">
              <span className="informe-summary-num">{comparativoMarcas.length}</span>
              <span className="informe-summary-label">marcas comparadas</span>
            </div>
            <div className="informe-summary-card informe-summary-card--ok2">
              <span className="informe-summary-num">{marcasSubieron}</span>
              <span className="informe-summary-label">marcas con más versiones que la carga anterior</span>
            </div>
            <div className="informe-summary-card informe-summary-card--warn">
              <span className="informe-summary-num">{marcasBajaron}</span>
              <span className="informe-summary-label">marcas con menos versiones que la carga anterior</span>
            </div>
          </div>

          <div className="informe-toolbar">
            <div className="informe-metric-toggle">
              <button
                className={`informe-metric-btn ${metricaDashboard === 'versiones' ? 'informe-metric-btn--active' : ''}`}
                onClick={() => setMetricaDashboard('versiones')}
              >
                Versiones
              </button>
              <button
                className={`informe-metric-btn ${metricaDashboard === 'modelos' ? 'informe-metric-btn--active' : ''}`}
                onClick={() => setMetricaDashboard('modelos')}
              >
                Modelos
              </button>
            </div>
            <div className="informe-search">
              <Search size={14} />
              <input
                type="text"
                placeholder="Buscar marca…"
                value={busquedaDashboard}
                onChange={e => setBusquedaDashboard(e.target.value)}
              />
            </div>
            <span className="informe-toolbar-count">{filasDashboard.length} marcas</span>
          </div>

          {chartDashboard.length === 0 ? (
            <div className="informe-state">
              <div className="informe-state-icon">🔍</div>
              <p>Sin resultados para el filtro actual.</p>
            </div>
          ) : (
            <div className="informe-chart-wrap">
              <ResponsiveContainer width="100%" height={Math.max(260, chartDashboard.length * 34)}>
                <BarChart data={chartDashboard} layout="vertical" margin={{ top: 4, right: 24, left: 4, bottom: 4 }} barCategoryGap="30%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="marca" width={110} tick={{ fontSize: 12, fill: '#374151' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    formatter={(value, name) => [value, name === 'anterior' ? 'Carga anterior' : 'Carga actual']}
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                  />
                  <Legend formatter={(value) => value === 'anterior' ? 'Carga anterior' : 'Carga actual'} wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="anterior" name="anterior" fill="#c7d2fe" radius={[0, 4, 4, 0]} maxBarSize={16} />
                  <Bar dataKey="actual" name="actual" fill="#4338ca" radius={[0, 4, 4, 0]} maxBarSize={16} />
                </BarChart>
              </ResponsiveContainer>
              {comparativoMarcas.length > chartDashboard.length && (
                <p className="informe-chart-note">Mostrando las {chartDashboard.length} marcas con más {metricaDashboard} en la carga actual — la tabla de abajo tiene el detalle completo.</p>
              )}
            </div>
          )}

          <div className="informe-table-wrap">
            <table className="informe-table">
              <thead>
                <tr>
                  <th>Marca</th>
                  <th style={{ textAlign: 'right' }}>Modelos (actual)</th>
                  <th style={{ textAlign: 'right' }}>Modelos (anterior)</th>
                  <th style={{ textAlign: 'right' }}>Versiones (actual)</th>
                  <th style={{ textAlign: 'right' }}>Versiones (anterior)</th>
                  <th>Comparado con</th>
                  <th style={{ textAlign: 'center' }}>Variación</th>
                </tr>
              </thead>
              <tbody>
                {filasDashboard.map(m => {
                  const delta = m.actual.versiones - m.anterior.versiones;
                  return (
                    <tr key={m.marca}>
                      <td><span className="informe-marca-badge">{m.marca}</span></td>
                      <td style={{ textAlign: 'right' }}>{m.actual.modelos}</td>
                      <td style={{ textAlign: 'right', color: '#9ca3af' }}>{m.anterior.modelos}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{m.actual.versiones}</td>
                      <td style={{ textAlign: 'right', color: '#9ca3af' }}>{m.anterior.versiones}</td>
                      <td style={{ fontSize: 12, color: '#9ca3af' }}>{m.anterior.fecha ? fmtFecha(m.anterior.fecha) : 'sin datos previos'}</td>
                      <td style={{ textAlign: 'center' }}>
                        {delta > 0 ? (
                          <span className="informe-delta informe-delta--up"><TrendingUp size={12} /> +{delta}</span>
                        ) : delta < 0 ? (
                          <span className="informe-delta informe-delta--down"><TrendingDown size={12} /> {delta}</span>
                        ) : (
                          <span className="informe-delta informe-delta--flat"><Minus size={12} /> 0</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!loading && !error && tab === 'ultima' && (
        <>
          <div className="informe-summary">
            <div className="informe-summary-card informe-summary-card--ok">
              <span className="informe-summary-num">{fmtFecha(ultimaCarga?.loadedAt)}</span>
              <span className="informe-summary-label">fecha de la última carga</span>
            </div>
            <div className="informe-summary-card">
              <span className="informe-summary-num">{marcasUnicasUltima}</span>
              <span className="informe-summary-label">marcas cargadas</span>
            </div>
            <div className="informe-summary-card">
              <span className="informe-summary-num">{filasUltimaCarga.length}</span>
              <span className="informe-summary-label">modelos/versiones cargados</span>
            </div>
          </div>

          <div className="informe-toolbar">
            <div className="informe-search">
              <Search size={14} />
              <input
                type="text"
                placeholder="Buscar marca o modelo…"
                value={busquedaUltima}
                onChange={e => setBusquedaUltima(e.target.value)}
              />
            </div>
            <span className="informe-toolbar-count">{filasUltimaCarga.length} filas</span>
          </div>

          <div className="informe-table-wrap">
            {filasUltimaCarga.length === 0 ? (
              <div className="informe-state">
                <div className="informe-state-icon">🔍</div>
                <p>Sin resultados para el filtro actual.</p>
              </div>
            ) : (
              <table className="informe-table">
                <thead>
                  <tr>
                    <th className="informe-th-ordenable" onClick={() => toggleOrdenUltima('marca')}>Marca {flechaOrden('marca')}</th>
                    <th className="informe-th-ordenable" onClick={() => toggleOrdenUltima('model')}>Modelo {flechaOrden('model')}</th>
                    <th className="informe-th-ordenable" onClick={() => toggleOrdenUltima('modelDetail')}>Versión {flechaOrden('modelDetail')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filasUltimaCarga.map((f, i) => (
                    <tr key={i}>
                      <td><span className="informe-marca-badge">{f.marca}</span></td>
                      <td>{f.model}</td>
                      <td>{f.modelDetail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {!loading && !error && tab === 'historico' && (
        <>
          <div className="informe-summary">
            <div className="informe-summary-card">
              <span className="informe-summary-num">{fechasUsadas.length}</span>
              <span className="informe-summary-label">cargas comparadas</span>
            </div>
            <div className="informe-summary-card informe-summary-card--warn">
              <span className="informe-summary-num">{resumenPorMarca.length}</span>
              <span className="informe-summary-label">marcas con algún cambio</span>
            </div>
            <div className="informe-summary-card informe-summary-card--warn">
              <span className="informe-summary-num">{marcasRecurrentes}</span>
              <span className="informe-summary-label">marcas con cambios recurrentes (2+)</span>
            </div>
          </div>

          <div className="informe-toolbar">
            <label className="informe-checkbox">
              <input type="checkbox" checked={soloRecurrentes} onChange={e => setSoloRecurrentes(e.target.checked)} />
              Mostrar solo marcas con cambios recurrentes (2+)
            </label>
            <div className="informe-search">
              <Search size={14} />
              <input
                type="text"
                placeholder="Buscar marca…"
                value={busquedaHistorico}
                onChange={e => setBusquedaHistorico(e.target.value)}
              />
            </div>
            <span className="informe-toolbar-count">{filasHistorico.length} marcas</span>
          </div>

          <div className="informe-table-wrap">
            {filasHistorico.length === 0 ? (
              <div className="informe-state">
                <div className="informe-state-icon">✅</div>
                <p>{soloRecurrentes
                  ? 'No hay marcas con cambios recurrentes en este período.'
                  : 'Sin resultados para el filtro actual.'}</p>
              </div>
            ) : (
              <table className="informe-table">
                <thead>
                  <tr>
                    <th style={{ width: 28 }}></th>
                    <th>Marca</th>
                    <th>Nuevos modelos</th>
                    <th>Modelos faltantes</th>
                    <th>Último cambio</th>
                  </tr>
                </thead>
                <tbody>
                  {filasHistorico.map(m => {
                    const ultimoCambio = [m.ultimaFechaNuevo, m.ultimaFechaFalla].filter(Boolean).sort().pop();
                    return (
                      <React.Fragment key={m.marca}>
                        <tr
                          className="informe-row-marca"
                          onClick={() => setMarcaExpandida(marcaExpandida === m.marca ? null : m.marca)}
                        >
                          <td>{marcaExpandida === m.marca ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                          <td><span className="informe-marca-badge">{m.marca}</span></td>
                          <td>
                            <span className={`informe-badge ${m.vecesConNuevos >= 2 ? 'informe-badge--info' : 'informe-badge--ok'}`}>
                              {m.totalNuevos}
                            </span>
                          </td>
                          <td>
                            <span className={`informe-badge ${m.vecesConFaltantes >= 2 ? 'informe-badge--warn' : 'informe-badge--ok'}`}>
                              {m.vecesConFaltantes >= 2 && <AlertTriangle size={12} />} {m.totalFaltantes}
                            </span>
                          </td>
                          <td>{fmtFecha(ultimoCambio)}</td>
                        </tr>
                        {marcaExpandida === m.marca && (
                          <tr className="informe-row-detalle">
                            <td colSpan={5}>
                              <table className="informe-subtable">
                                <thead>
                                  <tr><th>Tipo</th><th>Modelo</th><th>Versión</th><th>Carga anterior</th><th>Carga actual</th></tr>
                                </thead>
                                <tbody>
                                  {detalleUnificado(m).map((d, i) => (
                                    <tr key={i}>
                                      <td>
                                        <span className={`informe-badge ${d.tipo === 'nuevo' ? 'informe-badge--info' : 'informe-badge--warn'}`}>
                                          {d.tipo === 'nuevo' ? 'Nuevo' : 'Faltante'}
                                        </span>
                                      </td>
                                      <td>{d.model}</td>
                                      <td>{d.modelDetail}</td>
                                      <td>{fmtFecha(d.fechaAnterior)}</td>
                                      <td>{fmtFecha(d.fechaActual)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default InformeCarga;
